import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CHAIN_INTERACTION_KEY } from '@/constants/availability-telemetry.ts';

import { useAppointmentChainConfigs } from '@/hooks/admin/use-appointment-chain-configs.ts';
import { useBookAppointmentChain } from '@/hooks/use-book-appointment-chain.ts';
import { useChainAvailability } from '@/hooks/use-chain-availability.ts';
import useConfig from '@/hooks/useConfig.ts';

import { settleAvailabilityInteraction, startAvailabilityInteraction } from '@/lib/availability-telemetry.ts';

import type {
    ChainAutoSuggestionRequestStep,
    ChainAvailabilityItem,
    ChainAvailabilityRequestStep,
} from '@/models/appointment/AppointmentChainAvailability.ts';
import { ChainBookingStep, ChainPlanningMode } from '@/models/appointment/AppointmentChainConfig.ts';
import { AvailabilityAction, MetricSource } from '@/models/metrics.ts';

import { MIN_STEPS } from '@/schemas/ChainConfigForm.schema.ts';

import { showErrorNotification } from '@/services/notificationService.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { computeChainStatuses, countUnavailableSteps } from '@/utils/appointmentUtils.ts';
import { getEligibleMainPractitioners, hasActiveMarkedSteps } from '@/utils/chain-main-practitioner-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

const startChainInteraction = (action: AvailabilityAction): void => {
    startAvailabilityInteraction({ key: CHAIN_INTERACTION_KEY, mode: 'chain', action: action });
};

// An empty answer is a success: the check worked and the answer was no.
const settleChainSuccess = (source: MetricSource): void => {
    settleAvailabilityInteraction({ key: CHAIN_INTERACTION_KEY, outcome: 'success', source: source });
};

// Nothing was fetched from anywhere, so there is no source to report.
const settleChainConfigRefusal = (): void => {
    settleAvailabilityInteraction({
        key: CHAIN_INTERACTION_KEY,
        outcome: 'failure',
        reason: 'availability_config_incomplete',
    });
};

interface UseChainFooterResult {
    chainBookingStep: ChainBookingStep;
    cannotProceed: boolean;
    cannotBook: boolean;
    isBooking: boolean;
    isFetching: boolean;
    hasSuggestion: boolean;
    hasPreviousSuggestion: boolean;
    showDuplicationPopup: boolean;
    setShowDuplicationPopup: (visible: boolean) => void;
    handleNext: () => Promise<void>;
    handleAutoSuggest: () => Promise<void>;
    handleNextSuggestion: () => Promise<void>;
    handlePreviousSuggestion: () => void;
    handleBack: () => void;
    handleBook: () => Promise<void>;
    handleConfirmDuplication: () => Promise<void>;
}

export const useChainFooter = (): UseChainFooterResult => {
    const { customer_id: customerId } = useConfig();
    const { chainConfigs } = useAppointmentChainConfigs({
        customerId: customerId,
        includeEligiblePractitioners: true,
    });
    const { fetchAvailability, fetchAutoSuggestion, isFetching } = useChainAvailability();
    const { bookChain } = useBookAppointmentChain();
    const [showDuplicationPopup, setShowDuplicationPopup] = useState(false);

    const {
        chain_id: chainId,
        insurance_type: insuranceType,
        start_date: startDate,
        planning_mode: planningMode,
        removed_orders: removedOrders,
        calendar_selections: calendarSelections,
        main_practitioner_id: mainPractitionerId,
    } = useBookingStore(useShallow((state) => state.chain_selection));
    const isReturning = useBookingStore((state) => state.is_returning_patient);
    const chainBookingStep = useBookingStore((state) => state.chain_booking_step);
    const availability = useBookingStore((state) => state.chain_availability);
    const isBooking = useBookingStore((state) => state.is_booking_loading);
    const currentPatient = useBookingStore((state) => state.current_patient);
    const suggestionHistory = useBookingStore((state) => state.chain_suggestion_history);
    const setChainBookingStep = useBookingStore((state) => state.setChainBookingStep);
    const setChainAvailability = useBookingStore((state) => state.setChainAvailability);
    const setChainPlanningMode = useBookingStore((state) => state.setChainPlanningMode);
    const setChainSuggestionHistory = useBookingStore((state) => state.setChainSuggestionHistory);

    const selectedChain = chainConfigs.find((c) => c.id === chainId);
    const visibleSteps = selectedChain?.steps.filter((s) => !removedOrders.includes(s.order)) ?? [];
    const hasMissingCalendar = visibleSteps.some((step) => !calendarSelections[step.order]);
    const isManualPath = planningMode === ChainPlanningMode.MANUAL;

    const activeMarkedSteps = selectedChain ? hasActiveMarkedSteps(selectedChain, removedOrders) : false;
    const eligibleMainPractitioners = selectedChain ? getEligibleMainPractitioners(selectedChain, removedOrders) : [];
    const mainPractitionerBlocked =
        activeMarkedSteps &&
        (eligibleMainPractitioners.length === 0 ||
            !eligibleMainPractitioners.some((p) => p.practitioner_id === mainPractitionerId));

    const cannotProceed =
        !selectedChain ||
        !insuranceType ||
        visibleSteps.length < MIN_STEPS ||
        isFetching ||
        hasMissingCalendar ||
        mainPractitionerBlocked;

    const statuses = computeChainStatuses(availability, visibleSteps, selectedChain);
    const unavailableCount = countUnavailableSteps(statuses);
    const availabilityMismatch = availability.length !== visibleSteps.length;
    const cannotBook =
        unavailableCount > 0 ||
        availabilityMismatch ||
        visibleSteps.length === 0 ||
        !currentPatient ||
        !chainId ||
        isBooking;
    const hasSuggestion = availability.length > 0;
    const hasPreviousSuggestion = suggestionHistory.length > 0;

    const runAutoSuggestion = async (anchorDate?: string, history: ChainAvailabilityItem[][] = []): Promise<void> => {
        if (!selectedChain || !customerId || !insuranceType) {
            settleChainConfigRefusal();
            return;
        }
        const steps: ChainAutoSuggestionRequestStep[] = visibleSteps.map((step) => ({
            step_order: step.order,
            pms_calendar_id: calendarSelections[step.order] ?? '',
        }));
        const items = await fetchAutoSuggestion({
            customer_id: customerId,
            insurance_type: insuranceType,
            is_returning: isReturning,
            instance_id: selectedChain.instance_id,
            chain_id: selectedChain.id,
            start_date: anchorDate,
            journey_type: 'office-app-book',
            steps: steps,
            main_practitioner_id: activeMarkedSteps ? mainPractitionerId : undefined,
        });
        if (items) {
            setChainPlanningMode(ChainPlanningMode.AUTO);
            setChainAvailability(items);
            setChainSuggestionHistory(history);
            setChainBookingStep(ChainBookingStep.SUGGEST);
            settleChainSuccess('network');
        }
    };

    const handleNext = async (): Promise<void> => {
        if (isFetching) return;

        startChainInteraction(isManualPath ? 'open_slots' : 'auto_suggest');

        if (!selectedChain || !customerId || !insuranceType) {
            settleChainConfigRefusal();
            return;
        }

        if (!isManualPath) {
            await runAutoSuggestion(startDate);
            return;
        }
        const steps: ChainAvailabilityRequestStep[] = visibleSteps.map((step) => ({
            step_order: step.order,
            pms_calendar_id: calendarSelections[step.order] ?? '',
        }));
        const items = await fetchAvailability({
            customer_id: customerId,
            insurance_type: insuranceType,
            instance_id: selectedChain.instance_id,
            chain_id: selectedChain.id,
            start_date: startDate,
            journey_type: 'office-app-book',
            steps: steps,
            main_practitioner_id: activeMarkedSteps ? mainPractitionerId : undefined,
        });
        if (items) {
            setChainAvailability(items);
            setChainBookingStep(ChainBookingStep.TUNE);
            settleChainSuccess('network');
        }
    };

    const handleAutoSuggest = async (): Promise<void> => {
        startChainInteraction('auto_suggest');
        await runAutoSuggestion(startDate);
    };

    const handleNextSuggestion = async (): Promise<void> => {
        if (isFetching) return;
        const firstStepOrder = visibleSteps[0]?.order;
        const firstSlot = availability.find((item) => item.step_order === firstStepOrder)?.start_date;
        if (!firstSlot) return;
        const nextAnchor = dayjs.utc(firstSlot).add(1, 'day').format('YYYY-MM-DD');
        startChainInteraction('next_suggestion');
        await runAutoSuggestion(nextAnchor, [...suggestionHistory, availability]);
    };

    // Measured even though this only pops a stack today, so a future refetch here lands in the same metric.
    const handlePreviousSuggestion = (): void => {
        if (isFetching) return;
        const previous = suggestionHistory.at(-1);
        if (!previous) return;
        startChainInteraction('previous_suggestion');
        setChainAvailability(previous);
        setChainSuggestionHistory(suggestionHistory.slice(0, -1));
        settleChainSuccess('cache');
    };

    const handleBack = (): void => {
        setChainSuggestionHistory([]);
        setChainBookingStep(ChainBookingStep.CONFIGURE);
    };

    const handleBookingResult = (result: Awaited<ReturnType<typeof bookChain>>): void => {
        if (result.needsDuplication) {
            setShowDuplicationPopup(true);
        }
    };

    const handleBook = async (): Promise<void> => {
        if (cannotBook) return;
        handleBookingResult(await bookChain());
    };

    const handleConfirmDuplication = async (): Promise<void> => {
        useBookingStore.setState({ patient_duplication_allowed: true });
        setShowDuplicationPopup(false);
        const result = await bookChain();
        if (result.needsDuplication) {
            useBookingStore.setState({ patient_duplication_allowed: false });
            showErrorNotification('Fehler bei der Patientenduplizierung. Bitte versuchen Sie es erneut.');
        }
    };

    return {
        chainBookingStep,
        cannotProceed,
        cannotBook,
        isBooking,
        isFetching,
        hasSuggestion,
        hasPreviousSuggestion,
        showDuplicationPopup,
        setShowDuplicationPopup,
        handleNext,
        handleAutoSuggest,
        handleNextSuggestion,
        handlePreviousSuggestion,
        handleBack,
        handleBook,
        handleConfirmDuplication,
    };
};
